import { Alert, Badge, Box, Button, Divider, Tab, Tabs, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import VNAssetEditor from '@/components/VNAssetEditor/VNAssetEditor';
import { useProgress } from '@/contexts/ProgressProvider';
import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';
import { useModalContext } from '@/modals/VNModalProvider';
import { BatchGet } from '@/services/FuelToolsApi/fuelToolsApi.service';
import { useAppDispatch } from '@/store';
import { AssetForm } from '@/store/forms';
import { applicationDataSelector } from '@/store/slices/application-data';
import { UpdateAssetForm } from '@/store/slices/asset-editor';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import useAppUtils from '@/utils/appUtils';

import { AssetSource, ChannelEditorStep, Permission } from '../../../enums';
import type { ClipUsers, Item } from '../../../models';
import { FuelEntityType, FuelPortalService } from '../../../services';
import { enhanceFetchedAsset } from '../../../pages/assets/asset-listing/enhanceFetchedAsset';
import TabPanelContainer from './TabPanelContainer';

interface IPros {
  // open: boolean;
  // onClose: () => void;
  clipUsers: ClipUsers;
  // goTo: (event, item, tab) => void;
  entity?: string;
}
const defaultTabList = [
  {
    id: 'channelVODUsage',
    label: 'Channels',
    entity: FuelEntityType.Channel,
    tableColumnName: 'name',
    description: 'Name of channels(s) this asset is included in its playlist',
  },
  {
    id: 'clipGroupIds',
    label: 'Clip Groups',
    entity: FuelEntityType.Clip,
    tableColumnName: 'title',
    description: 'Name of Clip Group(s)  this asset is included',
  },
  {
    id: 'adVariants',
    label: 'Ad Variants',
    entity: FuelEntityType.Clip,
    tableColumnName: 'title',
    description: 'Name of Ad Variant asset(s) this asset is the parent',
  },
  {
    id: 'subClips',
    label: 'Sub Clips',
    entity: FuelEntityType.Clip,
    tableColumnName: 'title',
    description: 'Names of Sub Clip(s) this asset is the parent',
  },
  {
    id: 'channelEPGUsage',
    label: 'Channel EPG',
    entity: FuelEntityType.Channel,
    tableColumnName: 'name',
    description: 'Name of Scheduled Channels(s) this asset is included in its playlist',
  },
  {
    id: 'channelAdFillerUsage',
    label: 'Channel Ad Filler',
    entity: FuelEntityType.Channel,
    tableColumnName: 'name',
    description: 'Name of channel(s) this asset is used as an Ad Break Filler',
  },
  {
    id: 'channelContentFillerUsage',
    label: 'Channel Content Filler',
    entity: FuelEntityType.Channel,
    tableColumnName: 'name',
    description: 'Name of channel(s) this asset is used as an Startup Recorded Filler',
  },
  {
    id: 'transitionTemplate',
    label: 'Transition Templates',
    entity: FuelEntityType.Transition,
    tableColumnName: 'name',
    description: 'Name of Transition Template this asset is included',
  },
  {
    id: 'assets',
    label: 'Assets',
    entity: FuelEntityType.Clip,
    tableColumnName: 'name',
    description: 'Name of Assets this asset is included',
  },
  {
    id: 'sites',
    label: 'Sites',
    entity: FuelEntityType.Site,
    tableColumnName: 'name',
    description: 'Name of Site this asset is included',
  },
  {
    id: 'liveEventIds',
    label: 'App Events',
    entity: FuelEntityType.LiveEvent,
    tableColumnName: 'name',
    description: 'Name of App event this asset is included',
  },
  {
    id: 'movies',
    label: 'Movies',
    entity: FuelEntityType.Clip,
    tableColumnName: 'name',
    description: 'Name of Movie this asset is included',
  },
  {
    id: 'series',
    label: 'Series',
    entity: FuelEntityType.Clip,
    tableColumnName: 'name',
    description: 'Name of Series this asset is included',
  },
  {
    id: 'seasons',
    label: 'Seasons',
    entity: FuelEntityType.Clip,
    tableColumnName: 'name',
    description: 'Name of Season this asset is included',
  },
  {
    id: 'extras',
    label: 'Extras',
    entity: FuelEntityType.Clip,
    tableColumnName: 'name',
    description: 'Name of Extra this asset is included',
  },
  {
    id: 'trailers',
    label: 'Trailers',
    entity: FuelEntityType.Clip,
    tableColumnName: 'name',
    description: 'Name of Trailer this asset is included',
  },
  {
    id: 'childSeasons',
    label: 'Child Seasons',
    entity: FuelEntityType.Channel,
    tableColumnName: 'name',
    description: 'Name of Child Seasons this asset is included',
  },
  {
    id: 'linkedChannels',
    label: 'Linked Channels',
    entity: FuelEntityType.Channel,
    tableColumnName: 'name',
    description: 'Name of Channels this asset is linked to.',
  },
  {
    id: 'enabledDestinations',
    label: 'Enabled Destinations',
    entity: FuelEntityType.Destination,
    tableColumnName: 'name',
    description: 'Name of Enabled Destinations this channel is included',
  },
  {
    id: 'liveGuideIds',
    label: 'Live Guide',
    entity: FuelEntityType.LiveGuide,
    tableColumnName: 'name',
    description: 'Name of Live Guide this asset is included',
  },
];
function a11yProps(id: string) {
  return {
    id: `simple-tab-${id}`,
    'aria-controls': `simple-tabpanel-${id}`,
  };
}

const areAllClipUsesEmpty = (clipUsers) => {
  if (!clipUsers) return true;

  return Object.values(clipUsers)
    .filter((v) => Array.isArray(v))
    .every((arr) => arr.length === 0);
};

const ClipUsersModal = ({ clipUsers, entity = 'Clip' }: IPros) => {
  const [value, setValue] = useState(null);
  // const [clipUsers, setClipUsers] = useState<ClipUsers>();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState();
  const [tabList, setTabList] = useState([]);
  const tabValueRef = useRef(null);
  const { setProgress } = useProgress();
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const modal = useModalContext();
  const flags = useFlags();
  const dispatch = useAppDispatch();
  const { vnConfirm, alert } = useAppUtils();
  const { ouData, categories, switchOuData } = useSelector(applicationDataSelector);
  const [openClipGroupModal, setOpenClipGroupModal] = useState(false);
  const [clipGroupAssetForm, setClipGroupAssetForm] = useState();
  const navigate = useNavigate();
  const { onClose } = useModalContext();

  // Clip tool Modal
  const [clipToolClipId, setClipToolClipId] = useState(null);
  const [isClipToolOpen, setIsClipToolOpen] = useState(false);

  const closeClipTool = () => {
    setIsClipToolOpen(false);
  };
  // End Clip Tool Modal

  // AdVariant Modal
  const [adVariantClipId, setAdVariantClipId] = useState(null);
  const [isAdVariantModalOpen, setIsAdVariantModalOpen] = useState(false);
  const openAdVariantModal = (item: Item) => {
    let adVariantId = null;
    if (item.sourceType === AssetSource.SubClip) {
      adVariantId = item.id;
    } else {
      adVariantId = item.parentId ? item.parentId : item.id;
    }
    if (adVariantId) {
      setAdVariantClipId(adVariantId);
      setIsAdVariantModalOpen(true);
    }
  };
  const closeAdVariantModal = () => {
    setIsAdVariantModalOpen(false);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    // console.log('handleChange', { event, newValue });
    setValue(newValue);
    tabValueRef.current = newValue;
    loadTabContent(newValue);
  };

  const loadTabContent = async (type) => {
    const tab = defaultTabList.find((t) => t.id === type);
    setTab(tab);
    setProgress(true);
    try {
      if (clipUsers && clipUsers[type] && clipUsers[type].length > 0) {
        let list = [];
        if (tab && tab.entity === FuelEntityType.Site) {
          const id = clipUsers[type][0];
          const res = await FuelPortalService.getEntityById(tab?.entity, id);
          if (res) {
            list.push(res);
          }
        } else if (tab && tab.entity === FuelEntityType.Destination) {
          const destinations = clipUsers[type].map((des) => {
            return {
              id: des,
              name: des,
              entityType: FuelEntityType.Destination,
            };
          });
          list = destinations;
        } else {
          list = await BatchGet(tab?.entity, clipUsers[type]);
        }

        // setChannels(channels);
        const deletedKey = tab?.entity === FuelEntityType.Channel ? 'deleted' : 'isDeleted';
        list = list.filter((item) => !item[deletedKey]);
        if (tab?.entity === FuelEntityType.Clip) {
          list = list.map((item) => {
            return {
              ...item,
              name: item.title,
            };
          });
        }
        setItems(list);
      } else {
        setItems([]);
      }
    } finally {
      setProgress(false);
    }
  };

  const onEditAssetClick = async (id: string) => {
    try {
      setProgress(true);
      let asset = await FuelPortalService.getClipById(id);
      asset = enhanceFetchedAsset(asset, permissions, flags, false, [], [], ouData);
      if (asset) {
        const assetForm = await AssetForm.createFromData(asset, permissions, ouData);
        dispatch(UpdateAssetForm(assetForm));

        if (assetForm.source === 'NestedChannel' && asset?.data?.subtype === 'Series') {
          setClipGroupAssetForm(assetForm);
          // setOpenSeriesModal(true);
        } else if (assetForm.source === 'ClipGroup') {
          setClipGroupAssetForm(assetForm);
          setOpenClipGroupModal(true);
        } else {
          modal.onOpen('Edit Asset', 'xl', <VNAssetEditor />, id);
        }
      }
    } catch (ex) {
      console.error(ex);
      alert('Error fetching asset.', 'error');
    } finally {
      setProgress(false);
    }
  };

  const goToAssociatedClipUsers = async (event, item, tabValue) => {
    // console.log('goToAssociatedClipUsers item', { event, item, tabValue, clipUsers });

    if (item && item.entityType === FuelEntityType.Channel) {
      const channelTab = {
        channelAdFillerUsage: ChannelEditorStep.AdBreakFillers,
        channelVODUsage: ChannelEditorStep.VodContent,
        channelEPGUsage: ChannelEditorStep.BroadcastEPGContent,
        channelContentFillerUsage: ChannelEditorStep.VodInterstitials,
      };
      const channelTabValue = tabValue ? channelTab[tabValue] : '';
      window.open(`/channelmanager/editor/${item.id}?tabValue=${channelTabValue}`, '_blank');
      return;
    }
    if (item && item.entityType === FuelEntityType.LiveGuide) {
      window.open(`/live/guide/editor/${item.id}`, '_blank');
      return;
    }
    if (item && item.entityType === FuelEntityType.Destination) {
      window.open(`/channelmanager/editor/${clipUsers.toolsClipId}?tabValue=${ChannelEditorStep.Destinations}`, '_blank');
      return;
    }
    if (
      event &&
      event.ctrlKey &&
      item &&
      item.entityType === FuelEntityType.Transition &&
      permissions.has(Permission.CreateTransitionTemplate)
    ) {
      window.open(`/transitiontemplates/editor/${item.id}`, '_blank');
      return;
    }
    // onClose();
    if (
      (item &&
        item.entityType === FuelEntityType.Clip &&
        item.data?.subtype === 'Unknown' &&
        item.source !== 'SubClip' &&
        item.source !== 'AdVariant' &&
        permissions.has(Permission.EditAsset)) ||
      (item && item.entityType === FuelEntityType.Clip && permissions.has(Permission.EditAsset) && tabValue === 'assets')
    ) {
      onClose();
      await onEditAssetClick(item.id);
    } else if (
      item &&
      item.entityType === FuelEntityType.Clip &&
      item.source === 'SubClip' &&
      item.parentId &&
      permissions.has(Permission.EditAsset) &&
      tabValue !== 'assets'
    ) {
      onClose();
      setClipToolClipId(item.parentId);
      setIsClipToolOpen(true);
    } else if (
      item &&
      item.entityType === FuelEntityType.Clip &&
      item.source === 'AdVariant' &&
      item.parentId &&
      permissions.has(Permission.EditAsset) &&
      tabValue !== 'assets'
    ) {
      onClose();
      openAdVariantModal(item);
    } else if (item && item.entityType === FuelEntityType.Transition && permissions.has(Permission.CreateTransitionTemplate)) {
      window.open(`/transitiontemplates/editor/${item.id}`, '_blank');
    } else if (item && item.entityType === FuelEntityType.LiveEvent && permissions.has(Permission.EditAsset)) {
      window.open(`/live/events/editor/${item.id}`, '_blank');
    } else if (item.entityType === FuelEntityType.Clip && item.data?.subtype === 'series') {
      window.open(`/series/editor/${item.id}`, '_blank');
    } else if (
      item.entityType === FuelEntityType.Clip &&
      (item.data?.subtype === 'seasons' || item.data?.subtype === 'extras' || item.data?.subtype === 'trailers')
    ) {
      navigate(`/series/list`);
      window.open(`/series/list`, '_blank');
    }
  };

  useEffect(() => {
    // console.log('clipUsers changed', { clipUsers });

    // aalt #4460 2025-09-23 filter out live event symbolic clips from assets
    if (clipUsers?.assets?.length > 0 && clipUsers?.liveEventIds?.length > 0) {
      clipUsers.assets = clipUsers.assets.filter((x) => clipUsers.liveEventIds.findIndex((y) => x === y) === -1);
    }

    const myTabList = cloneDeep(defaultTabList);

    // Keep all tabs for ClipGroup and NestedChannel, otherwise remove "channelVODUsage"
    // if (clipUsers.sourceType !== AssetSource.ClipGroup && clipUsers.sourceType !== AssetSource.NestedChannel) {
    //   myTabList = myTabList.filter((t) => t.id !== 'channelVODUsage');
    // }

    setTabList(myTabList);

    if (myTabList.length > 0) {
      let tabId = null;

      // Ensure the key exists in clipUsers and is a non-empty array
      if (Array.isArray(clipUsers[tabId]) && clipUsers[tabId].length > 0) {
        setValue(tabId);
        tabValueRef.current = tabId;
      } else {
        // Find the first non-empty array key
        const firstNonEmptyArrayKey = Object.entries(clipUsers).find(
          ([key, value]) => Array.isArray(value) && value.length > 0
        )?.[0];

        // Find the first non-empty array key, excluding "channelVODUsage"
        // const firstNonEmptyArrayKey = Object.entries(clipUsers).find(
        //   ([key, value]) => key !== 'channelVODUsage' && Array.isArray(value) && value.length > 0
        // )?.[0];

        if (firstNonEmptyArrayKey) {
          tabId = firstNonEmptyArrayKey;
        }

        setValue(tabId);
        tabValueRef.current = tabId;
      }

      loadTabContent(tabId);
    }
  }, [clipUsers]);

  const noClipUses = areAllClipUsesEmpty(clipUsers);
  // console.log('noClipUses', { noClipUses, clipUsers });

  return (
    <>
      <VnModalBody>
        <Box sx={{ flexGrow: 1, display: 'flex' }}>
          {noClipUses ? (
            <Alert
              severity="info"
              sx={{
                width: '100%',
                mt: 4,
                justifyContent: 'center',
                textAlign: 'center',
                fontSize: '16px',
              }}
            >
              No Uses Found
            </Alert>
          ) : (
            <>
              {tabList.length > 0 && value && (
                <Tabs
                  value={value ?? defaultTabList[0].id}
                  onChange={handleChange}
                  aria-label="Clip Users"
                  scrollButtons="auto"
                  variant="scrollable"
                  orientation="vertical"
                  textColor="secondary"
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: '#0db080',
                      color: '#0db080',
                    },
                  }}
                  sx={{ borderRight: 1, borderColor: 'divider', width: '375px' }}
                >
                  {tabList.map((tab) => {
                    return clipUsers[tab.id]?.length > 0 ? (
                      <Tab
                        key={tab.id}
                        value={tab.id}
                        label={
                          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                            <Typography sx={{ color: tab.id === value ? '#0db080 !important' : '', paddingRight: '16px' }}>
                              {' '}
                              {tab.label}
                            </Typography>
                            <Box>
                              <Badge
                                badgeContent={clipUsers[tab.id].length === 0 ? '0' : clipUsers[tab.id].length}
                                color={clipUsers[tab.id].length > 0 ? 'success' : 'secondary'}
                              />
                            </Box>
                          </Box>
                        }
                        {...a11yProps(tab.id)}
                      />
                    ) : null;
                  })}
                </Tabs>
              )}
              {clipUsers && clipUsers[value] && tab && (
                <Box sx={{ paddingLeft: 2, height: '100%', alignItems: 'center', width: '100%' }}>
                  <Typography component="h2" style={{ fontSize: '18px !important' }}>
                    {tab.description}
                  </Typography>
                  <Divider />
                  <TabPanelContainer
                    entity={tab.entity}
                    items={items}
                    goToPage={(event, item) => goToAssociatedClipUsers(event, item, tabValueRef.current)}
                    columnName={tab.tableColumnName}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </VnModalBody>
      <VnModalFooter
        right={
          <Button autoFocus variant="contained" className="cancel-button" onClick={onClose}>
            Close
          </Button>
        }
      />
    </>
  );
};

export default ClipUsersModal;
